if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.destroy();
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button[href]');
      this.status = this.querySelector('.js-pagination-status');
      this.fetchInProgress = false;

      if (!this.paginationContainer || !this.paginationItems.length || !this.nextButton) return;

      this.clickHandler = this.onNextButtonClick.bind(this);
      this.nextButton.addEventListener('click', this.clickHandler);

      if (this.dataset.infiniteScrollEnabled === 'true') this.observePagination();
    }

    destroy() {
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
    }

    observePagination() {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.fetchNextItems();
      }, { rootMargin: `${window.innerHeight}px 0px` });
      this.intersectionObserver.observe(this.paginationContainer);
    }

    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    getFetchedScroll(template) {
      const paginationId = this.dataset.paginationId;
      if (paginationId) {
        const matchingScrolls = template.content.querySelectorAll('infinite-scroll[data-pagination-id]');
        const matchingScroll = Array.from(matchingScrolls).find((scroll) => scroll.dataset.paginationId === paginationId);
        if (matchingScroll) return matchingScroll;
      }
      return template.content.querySelector('infinite-scroll');
    }

    async fetchNextItems() {
      if (this.fetchInProgress || !this.nextButton?.href) return;

      const requestButton = this.nextButton;
      const originalButtonText = requestButton.textContent;
      const loadingText = requestButton.dataset.loadingText || window.strings?.general?.pagination?.loadingText || originalButtonText;
      this.fetchInProgress = true;
      this.setAttribute('aria-busy', 'true');
      requestButton.setAttribute('aria-disabled', 'true');
      requestButton.textContent = loadingText;
      if (this.status) this.status.textContent = loadingText;

      try {
        const response = await fetch(requestButton.href);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        const fetchedScroll = this.getFetchedScroll(template);
        const newItems = fetchedScroll?.querySelectorAll('.js-pagination__item');
        if (!newItems?.length) throw new Error('No pagination items were returned');

        const previousItemCount = this.paginationItems.length;
        const fragment = document.createDocumentFragment();
        newItems.forEach((item) => fragment.append(item.cloneNode(true)));
        this.paginationItems[this.paginationItems.length - 1].after(fragment);
        this.paginationItems = this.querySelectorAll('.js-pagination__item');

        this.updatePaginationControls(fetchedScroll);
        if (this.status) this.status.textContent = '';

        setTimeout(() => {
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
          window.renderSelects?.();
          window.loadDesktopOnlyTemplates?.();
          window.stickersReinit?.();

          if (document.body.classList.contains('tab-active') && this.dataset.infiniteScrollEnabled !== 'true') {
            this.paginationItems[previousItemCount]?.querySelector('a')?.focus();
          }
        }, 300);
      } catch (error) {
        console.error('Failed to load new items:', error);
        if (this.status) this.status.textContent = requestButton.dataset.errorText || '';
      } finally {
        this.fetchInProgress = false;
        this.removeAttribute('aria-busy');
        if (this.nextButton) {
          this.nextButton.removeAttribute('aria-disabled');
          if (this.nextButton === requestButton) this.nextButton.textContent = originalButtonText;
        }
      }
    }

    updatePaginationControls(fetchedScroll) {
      const nextPagination = fetchedScroll?.querySelector('.js-pagination');
      if (nextPagination?.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.nextButton = this.querySelector('.js-pagination__next-button[href]');
        this.status = this.querySelector('.js-pagination-status');
        this.nextButton.addEventListener('click', this.clickHandler);
        if (this.dataset.infiniteScrollEnabled === 'true') this.observePagination();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    cleanupAfterLastPage() {
      this.intersectionObserver?.disconnect();
      this.paginationContainer?.remove();
      this.querySelectorAll('.js-when-paginated-only').forEach((element) => element.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
